/**
 * Types for the get_school_applications RPC
 * Used by the ApplicationsTab to fetch data with server-side filtering, sorting, and pagination
 */

/**
 * Parameters for the get_school_applications RPC
 */
export interface GetSchoolApplicationsParams {
  // School targeting - use one or the other
  p_school_id?: string | null;
  p_school_ids?: string[] | null;

  // Core filters
  p_academic_year?: string | null;
  p_statuses?: string[];

  // Search
  p_search_query?: string | null;

  // Filters
  p_grade?: string | null;
  p_has_sibling?: boolean | null;
  p_needs_transportation?: boolean | null;
  p_special_transportation_needs?: boolean | null;

  // Sorting
  p_sort_field?: 'student_first_name' | 'student_last_name' | 'student_middle_name' | 'applying_grade' | 'status' | 'created_at' | 'submitted_at' | 'student_name' | 'grade' | 'address' | 'parent_name' | 'parent_first_name' | 'parent_last_name' | 'home_district' | 'current_school' | 'boundary_school';
  p_sort_direction?: 'asc' | 'desc';

  // Pagination
  p_limit?: number;
  p_offset?: number;
}

/**
 * Raw row returned from the RPC
 */
export interface SchoolApplicationRow {
  // Core fields
  id: string;
  student_first_name: string;
  student_middle_name: string | null;
  student_last_name: string;
  date_of_birth: string | null;
  applying_grade: string;
  current_grade: string | null;
  gender: string | null;
  ethnic_background: string | null;
  status: string;
  confirmation_code: string;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  academic_year: string;
  first_choice_session: string | null;
  second_choice_session: string | null;
  placed_session: string | null;

  // Parent info
  parent_first_name: string;
  parent_last_name: string;
  parent_email: string;
  parent_phone: string;
  parent_relationship: string | null;

  // Address
  address: string;
  apartment_number: string | null;
  city: string;
  state: string;
  zip_code: string;

  // Transportation
  needs_transportation: boolean;
  transportation_type: string | null;
  transportation_status: string | null;
  special_transportation_needs: string | null;
  bus_number: string | null;
  route_number: string | null;

  // Sibling info
  has_sibling: boolean;
  sibling_name: string | null;
  sibling_grade: string | null;

  // School references (joined)
  first_choice_id: string | null;
  first_choice_name: string | null;
  second_choice_id: string | null;
  second_choice_name: string | null;
  placed_school_id: string | null;
  placed_school_name: string | null;

  // Additional fields
  home_district: string | null;
  current_school: string | null;
  boundary_school: string | null;
  is_out_of_district: boolean | null;
  is_staff: boolean | null;
  parent_is_staff: boolean | null;
  mars_code: string | null;
  notes: string | null;

  // Pagination info
  total_count: number;
}

/**
 * Transformed application matching the ApplicationWithSession interface
 * Used by the UI components
 */
export interface TransformedApplication {
  id: string;
  student_first_name: string;
  student_middle_name?: string;
  student_last_name: string;
  date_of_birth?: string;
  applying_grade: string;
  current_grade?: string;
  gender?: string;
  ethnic_background?: string;
  status: string;
  confirmation_code: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  academic_year: string;
  session?: string | null;
  first_choice_session?: string | null;
  second_choice_session?: string | null;
  placed_session?: string | null;

  // Parent info
  parent_first_name: string;
  parent_last_name: string;
  parent_email: string;
  parent_phone: string;
  parent_relationship?: string;
  relationship_to_student?: string;

  // Address
  address: string;
  apartment_number?: string;
  city: string;
  state: string;
  zip_code: string;

  // Transportation
  needs_transportation: boolean;
  transportation_type?: string;
  transportation_status?: string;
  special_transportation_needs?: string;
  bus_number?: string;
  route_number?: string;

  // Sibling info
  has_sibling: boolean;
  sibling_name?: string;
  sibling_grade?: string;

  // School references as objects (matching existing interface)
  first_choice?: { id: string; name: string } | null;
  second_choice?: { id: string; name: string } | null;
  placed_school?: { id: string; name: string } | null;

  // Additional fields
  home_district?: string;
  current_school?: string;
  boundary_school?: string;
  is_out_of_district?: boolean;
  is_resident?: boolean;
  is_staff_child?: boolean;
  mars_code?: string;
  notes?: string;
}

/**
 * Transform raw RPC row to match existing ApplicationWithSession interface
 */
export function transformApplicationRow(row: SchoolApplicationRow): TransformedApplication {
  return {
    id: row.id,
    student_first_name: row.student_first_name,
    student_middle_name: row.student_middle_name || undefined,
    student_last_name: row.student_last_name,
    date_of_birth: row.date_of_birth || undefined,
    applying_grade: row.applying_grade,
    current_grade: row.current_grade || undefined,
    gender: row.gender || undefined,
    ethnic_background: row.ethnic_background || undefined,
    status: row.status,
    confirmation_code: row.confirmation_code,
    created_at: row.created_at,
    updated_at: row.updated_at,
    submitted_at: row.submitted_at || undefined,
    academic_year: row.academic_year,
    session: row.placed_session || row.first_choice_session,
    first_choice_session: row.first_choice_session,
    second_choice_session: row.second_choice_session,
    placed_session: row.placed_session,

    parent_first_name: row.parent_first_name,
    parent_last_name: row.parent_last_name,
    parent_email: row.parent_email,
    parent_phone: row.parent_phone,
    parent_relationship: row.parent_relationship || undefined,
    relationship_to_student: row.parent_relationship || undefined,

    address: row.address,
    apartment_number: row.apartment_number || undefined,
    city: row.city,
    state: row.state,
    zip_code: row.zip_code,

    needs_transportation: row.needs_transportation,
    transportation_type: row.transportation_type || undefined,
    transportation_status: row.transportation_status || undefined,
    special_transportation_needs: row.special_transportation_needs || undefined,
    bus_number: row.bus_number || undefined,
    route_number: row.route_number || undefined,

    has_sibling: row.has_sibling,
    sibling_name: row.sibling_name || undefined,
    sibling_grade: row.sibling_grade || undefined,

    first_choice: row.first_choice_id
      ? { id: row.first_choice_id, name: row.first_choice_name || '' }
      : null,
    second_choice: row.second_choice_id
      ? { id: row.second_choice_id, name: row.second_choice_name || '' }
      : null,
    placed_school: row.placed_school_id
      ? { id: row.placed_school_id, name: row.placed_school_name || '' }
      : null,

    home_district: row.home_district || undefined,
    current_school: row.current_school || undefined,
    boundary_school: row.boundary_school || undefined,
    is_out_of_district: row.is_out_of_district ?? undefined,
    is_resident: row.is_out_of_district === null ? undefined : !row.is_out_of_district,
    is_staff_child: row.is_staff || row.parent_is_staff || undefined,
    mars_code: row.mars_code || undefined,
    notes: row.notes || undefined,
  };
}

/**
 * Default actionable statuses for the Applications tab
 */
export const ACTIONABLE_STATUSES = ['pending_acceptance', 'accepted', 'enrolled'] as const;

/**
 * Default pagination values
 */
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
